import { useEffect, useState } from "react";
import {
  Anchor,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconTrash } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";
import moment from "moment";
import ActionCellRender from "../../components/actionCellRender";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
  PageList,
} from "../../components/pageList";
import { FormSectionCard, Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { buildEntityEditHref } from "../../utils/entityEditHref";

const apiEntity = "ongoingMonitoring";

const STATUSES = ["Not Start", "In process", "Completed"];
const STATUS_COLOR = {
  "Not Start": "gray",
  "In process": "blue",
  Completed: "green",
};

const dateValue = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const clientRecordId = (clientId) => {
  if (!clientId) return "";
  if (typeof clientId === "object") return String(clientId._id || clientId);
  return String(clientId);
};

const fileName = (file) => file?.name || file?.filename || "";

export const ongoingClientPageHref = (alert, { userRole, currentPathname } = {}) => {
  const id = clientRecordId(alert?.clientId);
  if (!id || !alert?.clientCode) return "";
  const href =
    buildEntityEditHref("client", id, {
      userRole,
      currentPathname,
    }) || "";
  if (!href) return "";
  const alertId = String(alert?.alertId ?? "").trim();
  const focus = `focus=nameScreening${alertId ? `&alertId=${encodeURIComponent(alertId)}` : ""}`;
  return `${href}&${focus}`;
};

const scanFacts = (values) => {
  const summary = values?.summary || {};
  const facts = [
    ["Batch", summary.batchId],
    ["Business Unit", summary.businessUnit],
    ["Region", summary.region],
    ["Scan Status", summary.status],
    ["Total Records", summary.totalRecords],
    ["Total Hits", summary.totalHitRecords],
    ["Imported On", summary.importedOn],
    ["Scanning Completed", summary.scanningCompletedOn],
    ["Scanned Result", fileName(values?.scannedFile)],
    ["Alert List", fileName(values?.alertFile)],
  ];
  if (Number(summary.suppressedRows) > 0) {
    facts.push(["Suppressed", String(summary.suppressedRows)]);
  }
  return facts.filter(([, value]) => String(value ?? "").trim());
};

export const OngoingMonitoringForm = ({ form }) => {
  const { t } = useTranslation();
  const auth = useAuthUser();
  const location = useLocation();
  const isNew = !form.values?._id;
  const clientHref = (alert) =>
    ongoingClientPageHref(alert, {
      userRole: auth()?.userRole,
      currentPathname: location.pathname,
    });

  const facts = isNew ? [] : scanFacts(form.values);

  return (
    <Stack spacing={0}>
      <FormSectionCard title="Details" mt={0} paperPadding="lg">
        <Stack>
          {isNew || !form.values.code ? null : (
            <TextInput label={t("Code")} value={form.values.code} readOnly />
          )}
          <DatePicker
            label={t("Open Date")}
            placeholder={t("Pick Date")}
            value={dateValue(form.values.openDate)}
            onChange={(value) => form.setFieldValue("openDate", value)}
            error={form.errors.openDate}
            required
          />
          <Textarea
            label={t("Remark")}
            value={form.values.remark || ""}
            onChange={(event) => form.setFieldValue("remark", event.currentTarget.value)}
            minRows={3}
          />
          {isNew ? null : (
            <Select
              label={t("Status")}
              data={STATUSES.map((status) => ({ value: status, label: t(status) }))}
              value={form.values.status || "Not Start"}
              onChange={(status) => form.setFieldValue("status", status || form.values.status)}
            />
          )}
        </Stack>
      </FormSectionCard>
      {isNew ? (
        <FormSectionCard title="Import" paperPadding="lg">
          <Stack>
            <Group>
              <Text size="sm" style={{ width: 140 }}>
                {t("Scanned Result")}
              </Text>
              <FileButton
                onChange={(file) => form.setFieldValue("scannedResult", file)}
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              >
                {(props) => (
                  <Button {...props} size="xs" variant="outline">
                    {t("Import")}
                  </Button>
                )}
              </FileButton>
              <Text size="xs" color={form.errors.scannedResult ? "red" : "dimmed"}>
                {form.values.scannedResult?.name || form.errors.scannedResult || ""}
              </Text>
            </Group>
            <Group>
              <Text size="sm" style={{ width: 140 }}>
                {t("Alert List")}
              </Text>
              <FileButton
                onChange={(file) => form.setFieldValue("alertList", file)}
                accept=".csv,text/csv"
              >
                {(props) => (
                  <Button {...props} size="xs" variant="outline">
                    {t("Import")}
                  </Button>
                )}
              </FileButton>
              <Text size="xs" color={form.errors.alertList ? "red" : "dimmed"}>
                {form.values.alertList?.name || form.errors.alertList || ""}
              </Text>
            </Group>
          </Stack>
        </FormSectionCard>
      ) : (
        <>
          {facts.length ? (
            <FormSectionCard title="Scan" paperPadding="lg">
              <SimpleGrid cols={4} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 2 }]}>
                {facts.map(([label, value]) => (
                  <Box key={label}>
                    <Text size="xs" color="dimmed">
                      {t(label)}
                    </Text>
                    <Text size="sm" mt={4}>
                      {value}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </FormSectionCard>
          ) : null}
          <FormSectionCard title="Alert" paperPadding="lg">
          <Table fontSize="xs" verticalSpacing="xs">
            <thead>
              <tr>
                <th>{t("Alert ID")}</th>
                <th>{t("Subject/Party")}</th>
                <th>{t("English Name")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {(form.values.alerts || []).map((alert) => {
                const href = clientHref(alert);
                return (
                  <tr key={alert._id || alert.alertId}>
                    <td>{alert.alertId}</td>
                    <td>
                      <Text size="xs">{alert.partyNumber}</Text>
                    </td>
                    <td>
                      <Text size="xs">{alert.englishName || "—"}</Text>
                    </td>
                    <td>
                      {href ? (
                        <Anchor
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                        >
                          {alert.clientCode}
                        </Anchor>
                      ) : (
                        <Text size="xs" color="dimmed">
                          {alert.clientCode || alert.submittedCode || "—"}
                        </Text>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          </FormSectionCard>
        </>
      )}
    </Stack>
  );
};

const formLayout = {
  containers: [
    {
      key: "main",
      parent: null,
      type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
      props: {},
    },
  ],
  fields: [
    {
      name: "editor",
      component: OngoingMonitoringForm,
      parent: "main",
      props: {},
    },
  ],
};

const formValues = {
  openDate: null,
  remark: "",
  status: "Not Start",
  scannedResult: null,
  alertList: null,
  alerts: [],
};

const formValidate = {
  openDate: (value) => (!value ? "Open date is required" : null),
  scannedResult: (value, values) =>
    values?._id || value ? null : "Scanned result is required",
  alertList: (value, values) => (values?._id || value ? null : "Alert list is required"),
};

const formSchema = {
  title: "Ongoing Monitoring",
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
    size: "xl",
  },
  apiEntity,
  initialValues: formValues,
  validate: formValidate,
  layout: formLayout,
  add: {
    title: "New Ongoing Monitoring",
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "lg",
    },
    apiEntity,
    initialValues: formValues,
    validate: formValidate,
    layout: formLayout,
  },
};

const OngoingMonitoring = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.FwdAml.nameScreeningEnabled()
      .then((result) => {
        if (!cancelled) setEnabled(!!result?.enabled);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableSchema = {
    showActionColumn: true,
    searchableFields: ["code", "remark", "status", "summary.batchId"],
    select: "code openDate remark status lastUpdateDate alertCount summary.batchId",
    byAggregation: false,
    columns: [
      {
        field: "code",
        headerName: "Code",
        sortable: true,
      },
      {
        field: "openDate",
        headerName: "Open Date",
        sortable: true,
        cellRender: (col, data) =>
          data.openDate ? moment(data.openDate).format("YYYY-MM-DD") : "",
      },
      {
        field: "alertCount",
        headerName: "Number of Alerts",
        sortable: true,
        cellRender: (col, data) => data.alertCount ?? 0,
      },
      {
        field: "lastUpdateDate",
        headerName: "Last Update Date",
        sortable: true,
        cellRender: (col, data) =>
          data.lastUpdateDate ? moment(data.lastUpdateDate).format("YYYY-MM-DD") : "",
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        cellRender: (col, data) => (
          <Badge color={STATUS_COLOR[data.status] || "gray"} variant="outline">
            {t(data.status || "Not Start")}
          </Badge>
        ),
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [{ name: "edit", icon: IconPencil, props: {} }],
        actionMenu: [
          {
            name: "delete",
            label: "Delete data",
            icon: IconTrash,
            props: { color: "red" },
          },
        ],
      },
    ],
  };

  const onSubmit = async ({ values, formMode }) => {
    if (formMode === "add") {
      try {
        const data = await api.FwdAml.importOngoing({
          openDate: values.openDate,
          remark: values.remark,
          scannedResult: values.scannedResult,
          alertList: values.alertList,
        });
        return { success: true, data };
      } catch (error) {
        return { success: false, error: { msg: error?.message || "Import failed" } };
      }
    }

    return api.updateById({
      apiEntity,
      id: values._id,
      values: {
        openDate: values.openDate,
        remark: values.remark || "",
        status: values.status,
      },
    });
  };

  if (enabled === null) return <Page title={t("Ongoing Monitoring")} />;
  if (!enabled) {
    return (
      <Page title={t("Ongoing Monitoring")}>
        <Text size="sm" color="dimmed">
          {t("FWD AML is not enabled")}
        </Text>
      </Page>
    );
  }

  return (
    <PageList
      title={t("Ongoing Monitoring")}
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
      initSort={{ by: "openDate", order: "desc" }}
      onSubmit={onSubmit}
    />
  );
};

export default OngoingMonitoring;
